import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2, Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import { inputCls, labelCls, btnPrimary, PanelHeader } from "./_shared";

export default function SectionsPanel() {
  const { adminApi } = useAuth();
  const { refresh } = useContent();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi
      .get("/content")
      .then((r) => {
        const s = { ...(r.data.sections || {}) };
        // Ensure project_types is always an array in state
        s.project_types = Array.isArray(s.project_types) ? s.project_types : [];
        setForm(s);
      })
      .catch(() => toast.error("Failed to load"));
  }, [adminApi]);

  if (!form) return <div className="text-white/50">Loading…</div>;

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const types = form.project_types || [];
  const setTypeAt = (idx, val) => {
    const next = [...types];
    next[idx] = val;
    setF("project_types", next);
  };
  const addType = () => setF("project_types", [...types, ""]);
  const removeType = (idx) => setF("project_types", types.filter((_, i) => i !== idx));
  const moveType = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= types.length) return;
    const next = [...types];
    [next[idx], next[j]] = [next[j], next[idx]];
    setF("project_types", next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.put("/content/sections", {
        services_subtitle: form.services_subtitle || "",
        gallery_subtitle: form.gallery_subtitle || "",
        footer_tagline: form.footer_tagline || "",
        social_proof_1: form.social_proof_1 || "",
        social_proof_2: form.social_proof_2 || "",
        social_proof_3: form.social_proof_3 || "",
        project_types: (form.project_types || []).map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Section copy updated");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="sections-panel">
      <PanelHeader
        eyebrow="08"
        title="Section Copy"
        description="Edit the short paragraphs shown next to each section heading on the homepage."
        actions={
          <button data-testid="sections-save" onClick={save} disabled={saving} className={btnPrimary}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
          </button>
        }
      />

      <div className="space-y-8">
        <div>
          <label className={labelCls}>Services — Subtitle (next to "Services · Metal · Steel · Concrete · Custom")</label>
          <textarea
            data-testid="sections-services-subtitle"
            className={`${inputCls} min-h-[110px]`}
            value={form.services_subtitle || ""}
            onChange={(e) => setF("services_subtitle", e.target.value)}
          />
        </div>

        <div>
          <label className={labelCls}>Gallery — Subtitle (next to "The Portfolio · Our Work Speaks Loud")</label>
          <textarea
            data-testid="sections-gallery-subtitle"
            className={`${inputCls} min-h-[110px]`}
            value={form.gallery_subtitle || ""}
            onChange={(e) => setF("gallery_subtitle", e.target.value)}
          />
        </div>

        <div>
          <label className={labelCls}>Footer — Tagline (small paragraph under the big "LC Builders LLC" wordmark)</label>
          <textarea
            data-testid="sections-footer-tagline"
            className={`${inputCls} min-h-[110px]`}
            value={form.footer_tagline || ""}
            onChange={(e) => setF("footer_tagline", e.target.value)}
          />
        </div>

        <div className="pt-6 border-t border-white/10 space-y-4">
          <div>
            <div className={labelCls}>Social Proof Banner (orange strip below the hero — 3 short chips)</div>
            <p className="text-xs text-white/40 mt-1 mb-3">Short, punchy phrases. Keep under ~35 characters each so they don't wrap on mobile.</p>
          </div>
          <div>
            <label className={labelCls}>Chip #1 (Star icon)</label>
            <input
              data-testid="sections-social-proof-1"
              className={inputCls}
              value={form.social_proof_1 || ""}
              onChange={(e) => setF("social_proof_1", e.target.value)}
              placeholder="100% Recommend · 8 Reviews"
            />
          </div>
          <div>
            <label className={labelCls}>Chip #2 (Users icon)</label>
            <input
              data-testid="sections-social-proof-2"
              className={inputCls}
              value={form.social_proof_2 || ""}
              onChange={(e) => setF("social_proof_2", e.target.value)}
              placeholder="859 Followers on Facebook"
            />
          </div>
          <div>
            <label className={labelCls}>Chip #3 (Thumbs-up icon)</label>
            <input
              data-testid="sections-social-proof-3"
              className={inputCls}
              value={form.social_proof_3 || ""}
              onChange={(e) => setF("social_proof_3", e.target.value)}
              placeholder="Honest Bids · On-Time Delivery"
            />
          </div>
        </div>

        <div className="pt-6 border-t border-white/10 space-y-3" data-testid="sections-project-types">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className={labelCls}>Estimate Form — Project Types</div>
              <p className="text-xs text-white/40 mt-1">
                These are the options in the "Project Type" dropdown on the Request-Free-Estimate form. Reorder with the arrows, remove with the trash, or click "Add Type" for a new one.
              </p>
            </div>
            <button
              type="button"
              onClick={addType}
              data-testid="sections-project-type-add"
              className="inline-flex items-center gap-2 border border-white/20 hover:border-[#FF6B00] hover:text-[#FF6B00] text-white text-xs font-display tracking-widest uppercase px-3 py-2 whitespace-nowrap transition-colors"
            >
              <Plus size={12} /> Add Type
            </button>
          </div>

          <div className="space-y-2">
            {types.map((t, idx) => (
              <div
                key={idx}
                data-testid={`sections-project-type-row-${idx}`}
                className="flex items-center gap-2 bg-[#121212] border border-white/10 pl-2 pr-1 py-1"
              >
                <span className="font-display text-white/40 text-xs w-6 text-center">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <input
                  className="flex-1 bg-transparent border-0 focus:outline-none focus:ring-0 text-white px-2 py-2 text-sm"
                  value={t}
                  data-testid={`sections-project-type-input-${idx}`}
                  onChange={(e) => setTypeAt(idx, e.target.value)}
                  placeholder="e.g. Custom Deer Blind"
                />
                <button
                  type="button"
                  onClick={() => moveType(idx, -1)}
                  disabled={idx === 0}
                  className="p-1.5 text-white/50 hover:text-[#FF6B00] disabled:opacity-30 disabled:pointer-events-none"
                  aria-label="Move up"
                  data-testid={`sections-project-type-up-${idx}`}
                >
                  <ArrowUp size={13} />
                </button>
                <button
                  type="button"
                  onClick={() => moveType(idx, 1)}
                  disabled={idx === types.length - 1}
                  className="p-1.5 text-white/50 hover:text-[#FF6B00] disabled:opacity-30 disabled:pointer-events-none"
                  aria-label="Move down"
                  data-testid={`sections-project-type-down-${idx}`}
                >
                  <ArrowDown size={13} />
                </button>
                <button
                  type="button"
                  onClick={() => removeType(idx)}
                  className="p-1.5 text-white/50 hover:text-red-500"
                  aria-label="Remove"
                  data-testid={`sections-project-type-remove-${idx}`}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
            {types.length === 0 && (
              <div className="text-sm text-white/40 border border-dashed border-white/15 p-6 text-center">
                No project types yet — click "Add Type" to add the first one.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
